import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../../store/hooks'
import {
  selectCrossClientMatchedWithoutActionsCount,
  selectCrossClientResultsExecution,
  selectCrossClientSelectedTab,
  selectCrossClientTabCounts,
} from '../../../store/crossClient/crossClientResults'
import { CROSS_CLIENT_INCOMPLETE_TAB, CrossClientQueryResultMatches } from '../../../models/CrossClient'
import { Button } from '../../../components/Button/Button'
import { getIntegrationSourceDisplayNames, joinNames } from '../utils'
import { CrossClientMatchActions } from './CrossClientMatchActions'
import { CrossClientIncompleteActions } from './CrossClientIncompleteActions'
import './CrossClientResultBanner.scss'

const clientWord = (count: number) => (count === 1 ? 'client' : 'clients')

export const CrossClientResultBanner: React.FC = () => {
  const navigate = useNavigate()

  const selectedTab = useAppSelector(selectCrossClientSelectedTab)
  const counts = useAppSelector(selectCrossClientTabCounts)
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const matchedWithoutActions = useAppSelector(selectCrossClientMatchedWithoutActionsCount)

  if (selectedTab === CrossClientQueryResultMatches.MATCH) {
    if (matchedWithoutActions <= 0) {
      return null
    }
    return (
      <div className='cross-client-result-banner'>
        <p className='paragraph-small fw-bold mb-0 zocks-text-gray-900'>
          {matchedWithoutActions.toLocaleString()} matching {clientWord(matchedWithoutActions)} with no actions yet. Review them and pick an action.
        </p>
        <CrossClientMatchActions />
      </div>
    )
  }

  if (selectedTab === CrossClientQueryResultMatches.PARTIAL) {
    if (counts.partial <= 0) {
      return null
    }
    return (
      <div className='cross-client-result-banner'>
        <p className='paragraph-small fw-bold mb-0 zocks-text-gray-900'>
          {counts.partial.toLocaleString()} {clientWord(counts.partial)} need your judgment. The AI Assistant couldn't confirm a match. Review each one and
          decide whether to act.
        </p>
      </div>
    )
  }

  if (selectedTab === CROSS_CLIENT_INCOMPLETE_TAB) {
    if (counts.incomplete <= 0) {
      return null
    }
    const sources = joinNames(['Zocks', ...getIntegrationSourceDisplayNames(execution?.sources ?? [])])
    return (
      <div className='cross-client-result-banner'>
        <p className='paragraph-small fw-bold mb-0 zocks-text-gray-900'>
          {counts.incomplete.toLocaleString()} {clientWord(counts.incomplete)} couldn't be evaluated. Some data was missing across {sources}. Update their
          profiles to include them in future queries.
        </p>
        <CrossClientIncompleteActions />
      </div>
    )
  }

  if (counts.noMatch <= 0) {
    return null
  }

  const handleRefine = () => {
    navigate('/cross-client', { state: { userQuestion: execution?.userQuestion ?? '' } })
  }

  return (
    <div className='cross-client-result-banner'>
      <p className='paragraph-medium mb-0 zocks-text-gray-900'>
        {counts.noMatch.toLocaleString()} {clientWord(counts.noMatch)} didn't match. If this seems high, consider refining your question.
      </p>
      <Button variant='header' className='button-medium rounded-3 text-nowrap zocks-border-0.5 zocks-border-color-gray-300' onClick={handleRefine}>
        Refine question
      </Button>
    </div>
  )
}
